import { useMemo, useState } from "react";
import { BarChart3 } from "lucide-react";
import { brl, dataBR } from "@/lib/api";
import { EmptyState, Field, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { Input } from "@/components/ui/input";
import { Stat, COMMISSION_LABELS } from "./shared";

const inRange = (iso, from, to) => {
  const day = String(iso || "").slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

/** Relatórios de consulta: tudo calculado em cima dos registros já existentes. */
export const CreatorReports = ({ data }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [productId, setProductId] = useState("");

  const productOptions = (data.products || []).map((p) => ({ value: p.id, label: p.name }));
  const productName = (data.products || []).find((p) => p.id === productId)?.name || "";

  const report = useMemo(() => {
    const orders = (data.orders || []).filter(
      (o) => o.status !== "CANCELADO" && inRange(o.created_at, from, to)
    );
    const byProduct = {};
    let salesTotal = 0;
    orders.forEach((o) => {
      o.items.forEach((i) => {
        if (productId && i.product_id !== productId) return;
        const key = i.name;
        const row = (byProduct[key] = byProduct[key] || { name: key, quantity: 0, total: 0 });
        row.quantity += Number(i.quantity || 0);
        row.total += Number(i.total || 0);
        salesTotal += Number(i.total || 0);
      });
    });
    const commissions = (data.commissions || []).filter(
      (c) =>
        inRange(c.created_at, from, to) &&
        (!productId || c.product_id === productId)
    );
    const byStatus = {};
    commissions.forEach((c) => {
      byStatus[c.status] = round((byStatus[c.status] || 0) + Number(c.amount || 0));
    });
    const payouts = (data.payouts || []).filter((p) =>
      inRange(p.paid_at || p.created_at, from, to)
    );
    return {
      orders: orders.filter((o) => !productId || o.items.some((i) => i.product_id === productId)),
      salesTotal: round(salesTotal),
      products: Object.values(byProduct).sort((a, b) => b.quantity - a.quantity),
      commissions,
      byStatus,
      payouts,
      payoutTotal: round(payouts.reduce((s, p) => s + Number(p.amount || 0), 0)),
    };
  }, [data, from, to, productId]);

  const generated = round(report.commissions.reduce((s, c) => s + Number(c.amount || 0), 0));

  return (
    <div className="space-y-6" data-testid="creator-reports">
      <Surface className="p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="De">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   data-testid="creator-report-from" />
          </Field>
          <Field label="Até">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   data-testid="creator-report-to" />
          </Field>
          <Field label="Produto">
            <NativeSelect value={productId} onChange={setProductId}
                          placeholder="Todos os produtos" options={productOptions}
                          data-testid="creator-report-product" />
          </Field>
        </div>
        {(from || to || productId) && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Filtro: {from ? `de ${dataBR(from)}` : "desde o início"}{" "}
            {to ? `até ${dataBR(to)}` : "até hoje"}
            {productName ? ` · ${productName}` : ""}
          </p>
        )}
      </Surface>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Vendas no período" value={brl(report.salesTotal)}
              hint={`${report.orders.length} pedidos`} testid="creator-report-sales" />
        <Stat label="Comissões geradas" value={brl(generated)}
              hint={`${report.commissions.length} lançamentos`}
              testid="creator-report-commissions" />
        <Stat label="Comissões pagas" value={brl(report.byStatus.PAGA || 0)}
              hint="no período" testid="creator-report-paid" />
        <Stat label="Repasses" value={brl(report.payoutTotal)}
              hint={`${report.payouts.length} registros`} testid="creator-report-payouts" />
      </div>

      {report.products.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="Sem vendas no período escolhido"
          description="Ajuste as datas ou o produto para ver outro recorte."
          testid="creator-reports-empty"
        />
      ) : (
        <Surface className="p-4" data-testid="creator-report-products">
          <p className="font-display text-base">Quantidades vendidas por produto</p>
          <ul className="mt-3 divide-y divide-white/5">
            {report.products.map((p) => (
              <li key={p.name} className="flex items-center justify-between gap-2 py-2.5 text-xs">
                <span className="min-w-0 truncate">{p.name}</span>
                <span className="shrink-0 font-num">
                  {p.quantity} un · {brl(p.total)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Mais vendido no período: {report.products[0].name} ({report.products[0].quantity} un)
          </p>
        </Surface>
      )}

      <Surface className="p-4" data-testid="creator-report-commission-status">
        <p className="font-display text-base">Comissões por situação</p>
        <ul className="mt-3 space-y-1.5 text-xs">
          {Object.keys(report.byStatus).length === 0 && (
            <li className="text-muted-foreground">Nenhuma comissão no período.</li>
          )}
          {Object.entries(report.byStatus).map(([status, value]) => (
            <li key={status} className="flex items-center justify-between gap-2">
              <span>{COMMISSION_LABELS[status] || status}</span>
              <span className="font-num">{brl(value)}</span>
            </li>
          ))}
        </ul>
      </Surface>
    </div>
  );
};

function round(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}
